import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const ActiveAlertCard = ({ alert, onDismiss, onSnooze, priorityColor }) => {
  const navigate = useNavigate();

  const getAlertIcon = (alertType) => {
    switch (alertType) {
      case 'exceeded_limit':
        return 'AlertTriangle';
      case 'approaching_limit':
        return 'AlertCircle';
      case 'end_of_month_summary':
        return 'FileText';
      default:
        return 'Bell';
    }
  };

  const getAlertTypeLabel = (alertType) => {
    switch (alertType) {
      case 'exceeded_limit':
        return 'Budget Exceeded';
      case 'approaching_limit':
        return 'Approaching Limit';
      case 'end_of_month_summary':
        return 'Monthly Summary';
      default:
        return 'Alert';
    }
  };

  const priorityColorClasses = {
    destructive: 'bg-destructive/10 border-destructive text-destructive',
    warning: 'bg-warning/10 border-warning text-warning',
    primary: 'bg-primary/10 border-primary text-primary'
  };

  return (
    <div className={`bg-card rounded-lg border-2 p-20 md:p-24 shadow-elevation-2 ${priorityColorClasses?.[priorityColor]}`}>
      <div className="flex items-start gap-16">
        {/* Icon */}
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
          priorityColor === 'destructive' ? 'bg-destructive/20' :
          priorityColor === 'warning' ? 'bg-warning/20' : 'bg-primary/20'
        }`}>
          <Icon
            name={getAlertIcon(alert?.alertType)}
            size={24}
            color={`var(--color-${priorityColor})`}
          />
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="flex items-start justify-between mb-8">
            <div>
              <h3 className="text-lg font-semibold text-card-foreground mb-4">
                {getAlertTypeLabel(alert?.alertType)}
              </h3>
              <p className="text-sm text-muted-foreground">
                {alert?.budgetLimit?.category?.name || 'Unknown Category'} • {alert?.budgetLimit?.monthYear}
              </p>
            </div>
            <span className={`px-12 py-4 rounded text-xs font-medium ${
              priorityColor === 'destructive' ? 'bg-destructive text-destructive-foreground' :
              priorityColor === 'warning' ? 'bg-warning text-warning-foreground' : 'bg-primary text-primary-foreground'
            }`}>
              {alert?.percentageUsed?.toFixed(1)}% used
            </span>
          </div>

          {/* Message */}
          <p className="text-foreground mb-16">{alert?.message}</p>

          {/* Spending Details */}
          {alert?.amountSpent !== null && alert?.amountRemaining !== null && (
            <div className="grid grid-cols-2 gap-12 mb-16 p-12 bg-background/50 rounded-lg">
              <div>
                <p className="text-xs text-muted-foreground mb-4">Amount Spent</p>
                <p className="text-lg font-semibold text-foreground">${alert?.amountSpent?.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-4">Remaining</p>
                <p className={`text-lg font-semibold ${
                  alert?.amountRemaining < 0 ? 'text-destructive' : 'text-success'
                }`}>
                  ${Math.abs(alert?.amountRemaining)?.toFixed(2)}
                  {alert?.amountRemaining < 0 && ' over'}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-8">
            <button
              onClick={() => navigate('/budget-management')}
              className="px-16 py-8 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth text-sm font-medium min-h-[44px]"
            >
              View Budget
            </button>
            <button
              onClick={onSnooze}
              className="px-16 py-8 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth text-sm font-medium min-h-[44px]"
            >
              Snooze 24h
            </button>
            <button
              onClick={onDismiss}
              className="px-16 py-8 bg-background text-muted-foreground rounded-lg hover:bg-muted transition-smooth text-sm font-medium min-h-[44px]"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActiveAlertCard;